// client/src/components/auth/ForgotPasswordPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth/AuthProvider';
import useAlert from '../../hooks/useAlert';
import { useAuthMutations } from '../../hooks/useAuth';
import { Loader2, Mail, Phone } from 'lucide-react';
import AuthShell from './AuthShell';
import AuthInput from './AuthInput';

const formatTelefone = (value) => {
    const n = value.replace(/\D/g, '').substring(0, 11);
    if (n.length > 10) return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
    if (n.length > 6) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
    if (n.length > 2) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
    if (n.length > 0) return `(${n}`;
    return n;
};

const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const { setAlert } = useAlert();
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const { forgotPassword } = useAuthMutations();

    const [metodo, setMetodo] = useState('email');
    const [email, setEmail] = useState('');
    const [telefone, setTelefone] = useState('');
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, authLoading, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();

        if (metodo === 'email') {
            if (email === '') {
                setAlert('Por favor, insira seu email.', 'danger');
                return;
            }
            setIsSending(true);
            try {
                await forgotPassword.mutateAsync({ email });
                setAlert('Instruções de recuperação enviadas para seu email!', 'success');
            } catch (err) {
                setAlert(err.response?.data?.error || 'Erro ao solicitar recuperação de senha', 'danger');
            } finally {
                setIsSending(false);
            }
            return;
        }

        const telefoneDigitos = telefone.replace(/\D/g, '');
        if (telefoneDigitos.length < 10) {
            setAlert('Por favor, insira um telefone válido com DDD.', 'danger');
            return;
        }
        setIsSending(true);
        try {
            await forgotPassword.mutateAsync({ telefone: telefoneDigitos });
            setAlert('Instruções de recuperação enviadas!', 'success');
        } catch (err) {
            setAlert(err.response?.data?.error || 'Erro ao solicitar recuperação de senha', 'danger');
        } finally {
            setIsSending(false);
        }
    };

    const isLoading = authLoading || isSending;

    return (
        <AuthShell tagline="Recuperar senha">
            <h1>Recuperar Senha</h1>
            <p>Escolha como prefere recuperar sua senha. Enviaremos as instruções.</p>

            <div className="auth-method-switch">
                <button
                    type="button"
                    className={`auth-method-option${metodo === 'email' ? ' is-active' : ''}`}
                    onClick={() => setMetodo('email')}
                >
                    <Mail size={16} /> Email
                </button>
                <button
                    type="button"
                    className={`auth-method-option${metodo === 'telefone' ? ' is-active' : ''}`}
                    onClick={() => setMetodo('telefone')}
                >
                    <Phone size={16} /> Telefone
                </button>
            </div>

            <form onSubmit={onSubmit}>
                {metodo === 'email' ? (
                    <AuthInput
                        icon={Mail}
                        label="Email Registrado*"
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="seuemail@exemplo.com"
                    />
                ) : (
                    <AuthInput
                        icon={Phone}
                        label="Telefone Registrado*"
                        id="telefone"
                        type="text"
                        value={telefone}
                        onChange={(e) => setTelefone(formatTelefone(e.target.value))}
                        required
                        placeholder="(XX) XXXXX-XXXX"
                        maxLength="15"
                    />
                )}

                <button type="submit" className="auth-shell-submit" disabled={isLoading}>
                    {isLoading ? <Loader2 className="animate-spin" size={20} /> : metodo === 'email' ? <Mail size={20} /> : <Phone size={20} />}
                    <span>Enviar Solicitação</span>
                </button>
            </form>

            <div className="auth-shell-footer">
                Lembrou a senha? <Link to="/login">Faça login</Link>
                <br />
                Não tem uma conta? <Link to="/register">Registre-se</Link>
            </div>
        </AuthShell>
    );
};

export default ForgotPasswordPage;
